import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PagedResult } from '@shared/services/paged-result';
import { Permission } from '@users/domain/services/user.enums';
import { UserService } from '@users/domain/services/user.service';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { debounceTime, skip, take } from 'rxjs/operators';

import { AppMapClickData, AppOlMapMarkerData } from '../../../shared/components/ol-map/ol-map.component';
import { CustomerLocation } from '../../../shared/models/customer-location';
import { CustomerChain } from '../../../shared/models/customer-location-chain';
import { Equipment } from '../../../shared/models/equipment';
import { LanguageService } from '../../../shared/services/language.service';
import { CustomerChainsResult } from '../../services/customer-chains-result';
import {
  EquipmentListSearchCriteria,
  EquipmentListSearchCriteriaForView
} from '../../services/equipment-list-search-criteria.interface';
import { EquipmentService } from '../../services/equipment.service';
import {
  EquipmentsGroupedByCustomerLocationResult
} from '../../services/equipments-grouped-by-customer-location.result';
import { EquipmentCreateDialogComponent } from './equipment-create-dialog/equipment-create-dialog.component';
import { DEFAULT_INSTALLERS_SEARCH_CRITERIA, InstallerService } from '@shared/services/installer.service';
import { Installer } from '@shared/models/installer';
import { equipmentStatusInfo } from '@equipment/components/equipment-details/equipment-details.component';
import { FormControl } from '@angular/forms';
import { adminLoginAsUser } from '@users/domain/services/user.mutations';
import { CustomerLocationCategory } from '@shared/models/customer-location-category';
import { CustomerLocationService } from '@customer-locations/services/customer-location.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatExpansionPanel } from '@angular/material/expansion';
import { InstallerChain } from '@shared/models/installer-chain';
import { InstallerSearchCriteria } from '@shared/services/installer-search-criteria.interface';
import { InstallerChainService } from '@shared/services/installer-chain.service';

const INITIAL_CRITERIA: EquipmentListSearchCriteria = {
  filters: {
    term: '',
    customerChain: [],
    country: [],
    customerLocation: [],
    installers: [],
    status: 'all',
    equipmentStatus: [],
    onlyHavingActiveAlarms: false,
    controllerHardware: [],
    hasEnergyModule: null,
    categories: [],
    customerLocationSubscriptionState: null
  },
  pagination: {
    perPage: 10,
    page: 0
  }
};

@Component({
  selector: 'app-equipment-list',
  templateUrl: './equipment-list.component.html',
  styleUrls: ['./equipment-list.component.scss']
})
export class EquipmentListComponent implements OnInit, OnDestroy {
  protected coordsCustomerLocationCentered: string[];
  private installers: PagedResult<Installer>;
  private installerChains: any;

  constructor(
    public dialog: MatDialog,
    public equipmentService: EquipmentService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private languageService: LanguageService,
    private translateService: TranslateService,
    public userService: UserService,
    public installerService: InstallerService,
    public installerChainService: InstallerChainService,
    public customerLocationService: CustomerLocationService
  ) {
    this.equipmentsGroupedByCustomerLocation$$ = equipmentService.equipmentsGroupedByCustomerLocation$$;
    this.trInstant = this.translateService.instant;
    this.trInstant = this.trInstant.bind(this.translateService);
    this.crtLang = this.languageService.currentLanguage;
    this.criteria = JSON.parse(JSON.stringify(INITIAL_CRITERIA));
    this.statuses = [
      { value: 'all', displayValue: this.trInstant('website.equipment.list.tabs.all', this.crtLang) },
      {
        value: 'onlyHavingActiveAlarms',
        displayValue: this.trInstant('website.equipment.list.tabs.having_alarm', this.crtLang),
        criteria : {
          filters : {
            onlyActiveEquipment : true
          }
        }
      },
      {
        value: 'onlyEquipmentsWithForeseenControl',
        displayValue: this.trInstant('website.equipment.list.tabs.control_foreseen', this.crtLang),
        criteria : {
          filters : {
            onlyActiveEquipment : true
          }
        }
      },
      {
        value: 'notAttached',
        displayValue: this.trInstant('website.equipment.list.tabs.unassigned', this.crtLang)
      }
    ];
  }
  public Permission = Permission;
  public installersFilterCtrl: FormControl = new FormControl();
  public crtLang: string;
  public subscriptions: Subscription[] = [];
  public panelOpenState = true;
  // public totalAlarmsCount$: Observable<any> | undefined;
  public statusIndex = 0;
  public statuses: any[];
  public displayedColumns: string[] = ['equipmentNumber', 'name', 'alarmInfo', 'maintenanceDate'];
  public displayedUnassignedEquimentsColumns = ['equipmentNumber', 'name'];
  public isLoadingSearchEquipments = true;
  public inputSubject = new BehaviorSubject('');

  public customerLocationsWithEquipments: EquipmentsGroupedByCustomerLocationResult;
  public unassignedEquipments: PagedResult<Equipment>;
  public equipmentsGroupedByCustomerLocation$$: Observable<any>;
  public customerLocationChainsFilterOptions: CustomerChainsResult = null;
  public installersFilterOptions: Installer[] = null;
  public customerLocationsCountryFilterOptions: any = null;

  public customerLocationsMapCoords: Array<AppOlMapMarkerData>;
  public selectedMapMarkerCustomerLocation: CustomerLocation = null;
  public totalEquipmentsHavingAlarms: number;
  public customerChainsSearchOptions: CustomerChain[];
  public equipmentTypeSearchOptions: {hardwareVersion: string, label: string}[] = [{hardwareVersion: '1', label: 'DNI'}, {hardwareVersion: '2', label: 'PolarBox'}];
  public customerLocationSubscriptionStateOptions: {value: boolean, label: string}[] = [{value: null, label: ''},{value: true, label: 'Abonnée'}, {value: false, label: 'Non abonnée'}];
  public installersSearchOptions: Installer[];
  public customerLocationCategoriesSearchOptions: CustomerLocationCategory[];
  public statusOptions: {label: string, value: string[]}[] = [{label: 'normal', value: ['ok', 'normal']}, {label: 'fluid', value: ['statistical', 'level']}, {label: 'technical', value: ['technical', 'disconnected']}];
  public countriesSearchOptions: string[];
  public equipmentStatusInfo = equipmentStatusInfo;
  public trInstant: (key: string | Array<string>, interpolateParams?: Object) => string | any;
  public installersOptions: ({ name: string; options: Installer[] } | { name: string; options: InstallerChain[] })[];
  public hasAdminPermission: boolean;

  public criteria: EquipmentListSearchCriteria;
  public criteriaForView: EquipmentListSearchCriteriaForView = {
    filters: {
      customerChainsById: {},
      customerLocationsById: {},
      installersById: {},
      countries: [],
      categories: [],
      controllerHardwareById: {},
      equipmentStatus: [],
    }
  };
  public controllerHardwareCtrl: FormControl = new FormControl();
  public energyModuleCtrl: FormControl = new FormControl();


  protected readonly adminLoginAsUser = adminLoginAsUser;
  public categoryCtrl: FormControl = new FormControl();
  public isLoadingExport = false;
  public equipmentStatusCtrl: FormControl = new FormControl();
  public customerLocationSubscriptionStatusCtrl: FormControl = new FormControl();

  public customDisplayEnergyModule(options: {value: boolean|null, label: string}[], value: any): string|null  {
    return options.filter((option) => option.value ===  value && option.value !== null)[0]?.label ?? null;
  }




  async ngOnInit(): Promise<void> {
    const criteriaInstaller = DEFAULT_INSTALLERS_SEARCH_CRITERIA;
    criteriaInstaller.removeInstallerWithoutCustomerLocation = true;
    this.installers = await  this.installerService.getInstallers(criteriaInstaller);

    this.installerChains = await  this.installerChainService.retrieveAll();

    this.installersOptions  = [{name : 'website.customer_location.filters.installer_select' , options: this.installers.itemsPage as any[]}, {name : 'website.customer_location.filters.installer_chain_select' , options: this.installerChains?.items as any[]} ];

    this.criteriaForView.filters.equipmentStatus = _.keyBy(this.statusOptions, 'label') as any;

    this.hasAdminPermission = this.userService.loggedInUserHasPermissions([Permission.MatelexAdminPermission]);



    this.equipmentService.getEquipmentListFilterCustomerLocationChains();
    this.updateCustomerChainFilterValues();

    this.equipmentService.getEquipmentFilterCountries();
    this.updateCountryFilterValues();



    this.statusIndex = this.statuses.findIndex((el: any) => el.value === this.criteria.filters.status);
    const installerAndInstallerChain = [ ...this.installers?.itemsPage, ...this.installerChains?.items];

    if (Array.isArray(installerAndInstallerChain) ) {
      this.criteriaForView.filters.installersById = _.keyBy(installerAndInstallerChain, '_id') as any;
    } else {
      this.criteriaForView.filters.installersById = {};
    }

    this.criteriaForView.filters.controllerHardwareById = _.keyBy(this.equipmentTypeSearchOptions, 'hardwareVersion') as any;
    this.updateInstallerFilterValues();

    await this.updateCategoryFilterValues();
    this.criteriaForView.filters.categories = this.customerLocationCategoriesSearchOptions.map((customerLocationCategories) => customerLocationCategories.id);
    this.readFiltersFromRoute();
    this.updateData();

    this.subscriptions.push(
      this.inputSubject.pipe(skip(1), debounceTime(1000)).subscribe(() => {
        this.updateData();
      })
    );

    const customerChainsResult: CustomerChainsResult = await this.equipmentService.customerLocationChains$$.pipe(take(1)).toPromise();
    if (Array.isArray(customerChainsResult.customerChainsPage)) {
      this.criteriaForView.filters.customerChainsById = _.keyBy(customerChainsResult.customerChainsPage, '_id') as any;
    } else {
      this.criteriaForView.filters.customerChainsById = {};
    }


    const customerLocationsCountriesResult: string[] = await this.equipmentService.customerLocationsCountries$$.pipe(take(1)).toPromise();
    if (Array.isArray(customerLocationsCountriesResult)) {
      this.criteriaForView.filters.countries = customerLocationsCountriesResult;
    } else {
      this.criteriaForView.filters.customerLocationsById = {};
    }



    this.subscriptions.push(
      this.equipmentsGroupedByCustomerLocation$$.subscribe((customerLocationsWithEquipments: EquipmentsGroupedByCustomerLocationResult) => {
        if (!Array.isArray(customerLocationsWithEquipments.customerLocationsPage)) {
          return;
        }
        this.customerLocationsMapCoords = [];
        for (const customerLocation of customerLocationsWithEquipments.customerLocationsPage) {
          const markerColor: string = this.computeMarkerColor(customerLocation);
          this.customerLocationsMapCoords.push({
            lon: customerLocation.longitude as unknown as string,
            lat: customerLocation.latitude as unknown as string,
            attributes: { isMarker: true, markerColor, customerLocationId: customerLocation._id }
          });
        }
        this.customerLocationsWithEquipments = customerLocationsWithEquipments;
        this.customerLocationsWithEquipments.customerLocationsPage.map(cl => {
          cl.equipments.map(eq => {
            if (eq.main_device_id && eq.main_device_id?.hardware_version !== '1.0.0' && !eq.main_device_id.license_enabled) {
              this.userService.getDniLicense(eq.main_device_id.id.split('-')[1]).subscribe(res => {
                eq.licenceString = res;
              });
            }
          });
        });
        console.log('EquipmentListComponent ngOnInit this.customerLocationsMapCoords', this.customerLocationsMapCoords);
        this.isLoadingSearchEquipments = false;
      })
    );
    this.subscriptions.push(this.installersFilterCtrl.valueChanges.subscribe(async (value) => {
      this.criteria.filters.installers = value;
      this.updateInstallerFilterValues();
      this.updateData();
    }));

    this.subscriptions.push(this.energyModuleCtrl.valueChanges.subscribe((hasEnergyModule) => {
      this.criteria.filters.hasEnergyModule = hasEnergyModule;
      this.updateData();
    }));
    this.subscriptions.push(this.customerLocationSubscriptionStatusCtrl.valueChanges.subscribe((hasSubscription) => {
      this.criteria.filters.customerLocationSubscriptionState = hasSubscription;
      this.updateData();
    }));
  }

  public isPolarBoxNotConnected(equipment: Equipment): boolean {
    if (equipment.main_device_id?.hardware_version !== '1.0.0' && !equipment.main_device_id?.license_enabled ) {
      return true;
    } else {
      return false;
    }
  }
  public disableClickIfNotActive(equipmentGroup: CustomerLocation): boolean {
    return !this.hasAdminPermission && !equipmentGroup.is_active;
  }
  public displayEquipmentType(equipment: Equipment): string {
    if (equipment.main_device_id?.hardware_version !== '1.0.0') {
      return this.trInstant('website.equipment.list.table.polarbox_lbl', this.crtLang);
    } else {
      return this.trInstant('website.equipment.list.table.equipment_lbl', this.crtLang);
    }
  }
  /**
   * According to Confluence get the color of "Worst" EquipmentStatus (the most reddish one)
   */
  private computeMarkerColor(customerLocation: CustomerLocation): string {
    if (!Array.isArray(customerLocation.equipments)) {
      return null;
    }
    // return null;

    return customerLocation.equipments.sort((equipment1: Equipment, equipment2: Equipment) => {
      return equipment1?.status?.colorDistanceFromRed - equipment2?.status?.colorDistanceFromRed;
    })[0]?.status?.color;
  }

  public async onClickOnMap(mapClickEvent: AppMapClickData) {
    console.log('EquipmentListComponent onClickOnMap mapClickEvent', mapClickEvent);
    const equipmentsGroupedByCustomerLocation: EquipmentsGroupedByCustomerLocationResult = await this.equipmentsGroupedByCustomerLocation$$
      .pipe(take(1))
      .toPromise();
    if (mapClickEvent?.attributes?.isMarker && mapClickEvent?.attributes?.customerLocationId) {
      this.selectedMapMarkerCustomerLocation = equipmentsGroupedByCustomerLocation.customerLocationsPage.find(
        (customerLocation: CustomerLocation) => {
          return customerLocation._id === mapClickEvent?.attributes?.customerLocationId;
        }
      );

      console.log('EquipmentListComponent onClickOnMap this.selectedMapMarkerCustomerLocation', this.selectedMapMarkerCustomerLocation);
    } else {
      this.selectedMapMarkerCustomerLocation = null;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  public openCreateDialog(): void {
    const dialogRef = this.dialog.open(EquipmentCreateDialogComponent, {
      data: { name: 'test name', data: ' test data' }
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.updateData();
        }
      })
    );
  }

  public onPaginatorChange(event: PageEvent): void {
    this.criteria.pagination.perPage = event.pageSize;
    this.criteria.pagination.page = event.pageIndex;
    this.updateData();
  }

  public removeFilters(): void {
    const statusFilterBackup = JSON.parse(JSON.stringify(this.criteria.filters.status));
    this.criteria = JSON.parse(JSON.stringify(INITIAL_CRITERIA));
    this.criteria.filters.status = statusFilterBackup;
    this.updateCountryFilterValues();
    this.updateCustomerChainFilterValues();
    this.updateInstallerFilterValues();
    this.updateCategoryFilterValues();
    this.updateEquipmentStatusFilterValues();
    this.updateData();
  }



  public async updateData(): Promise<void> {
    if (this.statuses[this.statusIndex].value === 'notAttached') {
      try {
        this.unassignedEquipments = await this.equipmentService.getUnassignedEquipments(this.criteria);
      } catch (e) {
        console.log(e);
      }
      return;
    } else {
      this.unassignedEquipments = null;
    }
    this.isLoadingSearchEquipments = true;
    const criteriaToSend = JSON.parse(JSON.stringify(this.criteria));


    criteriaToSend.filters.equipmentStatus = criteriaToSend.filters?.equipmentStatus.map(
      (status) => (this.criteriaForView.filters.equipmentStatus[status] as any).value);

    criteriaToSend.filters.equipmentStatus = criteriaToSend.filters.equipmentStatus.flatMap((statusArray) => statusArray);
    criteriaToSend.filters.onlyActiveEquipment  = this.statuses[this.statusIndex]?.criteria?.filters?.onlyActiveEquipment;

    this.equipmentService.loadEquipmentsGroupedByCustomerLocation(criteriaToSend);

    this.selectedMapMarkerCustomerLocation = null;
    if (this.criteria.pagination.perPage === 1000) {
      this.criteria.pagination.perPage = 10;
    }
    this.applyFilterToRoute();
  }

  public async updateCustomerChainFilterValues(): Promise<void> {
    const customerChainsResult: CustomerChainsResult = await this.equipmentService.customerLocationChains$$.pipe(take(1)).toPromise();
    const filteredResult: CustomerChainsResult = Object.assign({}, customerChainsResult);
    filteredResult.customerChainsPage = customerChainsResult.customerChainsPage.filter(
      (clc: CustomerChain) => this.criteria.filters.customerChain.indexOf(clc._id) < 0
    );
    this.customerLocationChainsFilterOptions = filteredResult;
    this.customerChainsSearchOptions = [...filteredResult?.customerChainsPage];
    // this.customerLocationChainsFilterOptions$$.next(filteredResult);
  }

  public async updateInstallerFilterValues(): Promise<void> {
    const criteriaInstaller = DEFAULT_INSTALLERS_SEARCH_CRITERIA;
    criteriaInstaller.removeInstallerWithoutCustomerLocation = true;

    const installerPagedResult = await this.installerService.getInstallers(criteriaInstaller);
    const installerChainPagedResult: any = await this.installerChainService.retrieveAll();

    let installerFilteredResult: Installer[] = Object.assign([], installerPagedResult?.itemsPage);
    let installerChainFilteredResult: InstallerChain[] = Object.assign([], installerChainPagedResult?.items);

    installerFilteredResult = installerFilteredResult?.filter(
      (installer: any) => this.criteria.filters?.installers?.indexOf(installer._id) < 0
    );

    installerChainFilteredResult = installerChainFilteredResult?.filter(
      (installer: any) => this.criteria.filters?.installers?.indexOf(installer._id) < 0
    );


    this.installersOptions = [{name : 'website.customer_location.filters.installer_select' , options: installerFilteredResult}, {name : 'website.customer_location.filters.installer_chain_select' , options: installerChainFilteredResult} ];
  }

  public async updateCategoryFilterValues(): Promise<void> {
    const customerLocationCategories = await this.customerLocationService.getCustomerLocationCategories();
    this.customerLocationCategoriesSearchOptions = customerLocationCategories.filter(
      (customerLocationCategory: CustomerLocationCategory) => this.criteria.filters.categories.indexOf(customerLocationCategory.id) < 0
    );
    this.categoryCtrl.patchValue(null);
  }

  public async updateEquipmentStatusFilterValues(): Promise<void> {
    const customerLocationCategories =  [{label: 'normal', value: ['ok', 'normal']}, {label: 'fluid', value: ['statistical', 'level']}, {label: 'technical', value: ['technical', 'disconnected']}];
    this.statusOptions =     customerLocationCategories.filter((equipmentStatus) => {
      return  !this.criteria.filters.equipmentStatus.some((eqStatus) => equipmentStatus.label === eqStatus);
    });
    this.equipmentStatusCtrl.patchValue(null);
  }
  public async updateEquipmentTypeFilterValues(): Promise<void> {
    const defaultValue =  [{hardwareVersion: '1', label: 'DNI'}, {hardwareVersion: '2', label: 'PolarBox'}];
    this.equipmentTypeSearchOptions = defaultValue.filter(
      (equipmentType: {hardwareVersion: string, label: string}) => this.criteria.filters.controllerHardware.indexOf(equipmentType.hardwareVersion) < 0
    );
    this.controllerHardwareCtrl.patchValue(null);

  }



  public async updateCountryFilterValues(): Promise<void> {
    const customerLocationsCountries: string[] = await this.equipmentService.customerLocationsCountries$$.pipe(take(1)).toPromise();
    let filteredResult: string[] = [...customerLocationsCountries];
    filteredResult = filteredResult.filter((clc: string) => this.criteria.filters.country.indexOf(clc) < 0);
    this.countriesSearchOptions = [...filteredResult];
    this.customerLocationsCountryFilterOptions = filteredResult;
  }

    public handleAutoComplete($event: MatAutocompleteSelectedEvent, type: string): void {
    const output = $event.option.value;
    switch (type) {
      case 'cc':
        this.criteria.filters.customerChain.push(output);
        this.updateCustomerChainFilterValues();
        break;
      case'equipmentType':
        this.criteria.filters.controllerHardware.push(output);
        this.updateEquipmentTypeFilterValues();
        break;
      case'category':
        this.criteria.filters.categories.push(output);
        this.updateCategoryFilterValues();

        break;
      case'equipmentStatus':
        this.criteria.filters.equipmentStatus.push(output);
        this.updateEquipmentStatusFilterValues();

        break;
      default:
        this.criteria.filters.country.push(output);
        this.updateCountryFilterValues();
        break;
    }
    this.updateData();
  }
  public removeCustomerChainFilterKey(value: string): void {
    this.criteria.filters.customerChain.splice(this.criteria.filters.customerChain.indexOf(value), 1);
    this.updateCustomerChainFilterValues();
    this.updateData();
  }

  public removeCategoryFilterKey(value: string): void {
    this.criteria.filters.categories.splice(this.criteria.filters.categories.indexOf(value), 1);
    this.updateCategoryFilterValues();
    this.updateData();
  }
  public removeEquipmentStatusFilterKey(value: string): void {
    this.criteria.filters.equipmentStatus.splice(this.criteria.filters.equipmentStatus.indexOf(value), 1);
    this.updateEquipmentStatusFilterValues();
    this.updateData();
  }

  public removeEquipmentTypeFilterKey(value: string): void {
    this.criteria.filters.controllerHardware.splice(this.criteria.filters.controllerHardware.indexOf(value), 1);
    this.updateEquipmentTypeFilterValues();
    this.updateData();
  }

  public removeInstallersFilterKey(value: string): void {
    this.criteria.filters.installers.splice(this.criteria.filters.installers.indexOf(value), 1);
    this.updateInstallerFilterValues();
    this.installersFilterCtrl.patchValue(this.criteria.filters.installers, {emitEvent: false});
    this.updateData();
  }

  public removeCountryFilterKey(value: string): void {
    this.criteria.filters.country.splice(this.criteria.filters.country.indexOf(value), 1);
    this.updateCountryFilterValues();
    this.updateData();
  }

  public setStatusFilter(index: number): void {
    this.criteria.pagination.page = 0;
    this.criteria.filters.status = this.statuses[index].value;
    this.updateData();
  }

  public readFiltersFromRoute(): void {
    this.subscriptions.push(this.activatedRoute.queryParams.subscribe((params) => {
      console.log('test', params);
      if (!params.criteria){
        this.criteria = JSON.parse(JSON.stringify(INITIAL_CRITERIA));
        console.log('EquipmentListComponent readFiltersFromRoute 1 this.criteria ', this.criteria);
      }else{

        this.criteria = JSON.parse(params.criteria);
      }
    }));

  }



  public applyFilterToRoute(): void {
    console.log('EquipmentListComponent applyFilterToRoute filtersForRoute', 'filtersForRoute', this.criteria);
    if (this.router.url.includes('equipment') ){
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { criteria: JSON.stringify(this.criteria) },
        queryParamsHandling: 'merge',
      });
    }

  }


  public exportEquipments(): void {
    this.isLoadingExport = true;
    const criteriaToSend = JSON.parse(JSON.stringify(this.criteria));
    criteriaToSend.filters.equipmentStatus = criteriaToSend.filters?.equipmentStatus.map(
      (status) => (this.criteriaForView.filters.equipmentStatus[status] as any).value);

    criteriaToSend.filters.equipmentStatus = criteriaToSend.filters.equipmentStatus.flatMap((statusArray) => statusArray);

    this.equipmentService.equipmentsExportCsv(criteriaToSend.filters)
      .subscribe((response) =>
      {
        const contentDispositionHeader = response.headers.get('Content-Disposition');
        const filename = contentDispositionHeader.split(';').find((part: string ) => part.trim().startsWith('filename=')).split('=')[1];
        const blob = new Blob([response.body], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        this.isLoadingExport = false;
      });
  }

  locateOnMap(equipmentGroup: CustomerLocation): void {
    this.coordsCustomerLocationCentered = [String(equipmentGroup.longitude), String(equipmentGroup.latitude)];
  }


}
